import { Controller } from "@hotwired/stimulus"

// Per-column filter popover (rendered by Cup::DataTable::FilterPopover) —
// spec: docs/specs/overviews/_settings.md (the per-column filters section).
//
// - A trigger button toggles the panel; it closes on outside-click and Esc.
// - Text columns: the operator <select> rewrites the value <input>'s `name` to a
//   canonical Ransack predicate (q[<attr>_<predicate>]) and hides the value for
//   the value-less blank/present predicates ("is blank" / "is not blank").
// - Cancel restores the last-applied operator + value and closes.
// - Select columns (roles) carry no operator/value targets; the controller still
//   provides toggle + outside-click/Esc close.
export default class extends Controller {
  static targets = ["panel", "operator", "value", "valueWrap"]
  static values = { attribute: String, ownNames: Array }

  connect() {
    if (this.hasOperatorTarget) {
      this.initialOperator = this.operatorTarget.value
      this.update()
    }
    if (this.hasValueTarget) this.initialValue = this.valueTarget.value
    this.onDocClick = this.onDocClick.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
  }

  disconnect() {
    this.removeGlobalListeners()
  }

  toggle(event) {
    event.preventDefault()
    this.isOpen() ? this.close() : this.open()
  }

  open() {
    this.panelTarget.classList.remove("hidden")
    document.addEventListener("click", this.onDocClick)
    document.addEventListener("keydown", this.onKeydown)
    const field = this.hasValueTarget && !this.valueTarget.classList.contains("hidden")
      ? this.valueTarget
      : (this.hasOperatorTarget ? this.operatorTarget : null)
    field?.focus()
  }

  close() {
    this.panelTarget.classList.add("hidden")
    this.removeGlobalListeners()
  }

  // Cancel: discard any pending (not-yet-applied) edits, then close.
  cancel() {
    if (this.hasOperatorTarget) {
      this.operatorTarget.value = this.initialOperator
      this.update()
    }
    if (this.hasValueTarget) this.valueTarget.value = this.initialValue
    this.close()
  }

  // Stop clicks inside the panel from bubbling to the document close-handler.
  stop(event) {
    event.stopPropagation()
  }

  // Select-type filters (e.g. roles) auto-apply: a <select> change can't submit
  // a form natively, so request it (which fires submit -> #sync). Text filters
  // submit natively (Enter / the apply button) and route through #sync too.
  submit(event) {
    event.target.closest("form")?.requestSubmit()
  }

  // This column's GET form replaces the whole query string, so mirror the rest
  // of the current filter context (other columns, the toolbar search, sort,
  // workspace) from the URL into the form as hidden inputs just before submit.
  // Skip this column's own predicates (the live input supplies those) and
  // `page` (applying a filter returns to page 1).
  sync(event) {
    const form = event.target
    form
      .querySelectorAll('input[type="hidden"][data-column-filter-mirrored="true"]')
      .forEach((el) => el.remove())

    const own = new Set(this.ownNamesValue)
    const present = new Set(Array.from(form.elements).map((el) => el.name).filter(Boolean))

    for (const [name, value] of new URLSearchParams(window.location.search)) {
      if (name === "page" || own.has(name) || present.has(name)) continue
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      input.value = value
      input.dataset.columnFilterMirrored = "true"
      form.appendChild(input)
    }
  }

  isOpen() {
    return !this.panelTarget.classList.contains("hidden")
  }

  onDocClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  onKeydown(event) {
    if (event.key === "Escape") this.close()
  }

  removeGlobalListeners() {
    document.removeEventListener("click", this.onDocClick)
    document.removeEventListener("keydown", this.onKeydown)
  }

  update() {
    const predicate = this.operatorTarget.value
    const valueless = predicate === "blank" || predicate === "present"

    this.valueTarget.name = `q[${this.attributeValue}_${predicate}]`

    const wrap = this.hasValueWrapTarget ? this.valueWrapTarget : this.valueTarget
    if (valueless) {
      this.valueTarget.value = "true"
      wrap.classList.add("hidden")
    } else {
      wrap.classList.remove("hidden")
      // Don't carry the sentinel "true" forward into a textual predicate.
      if (this.valueTarget.value === "true") this.valueTarget.value = ""
    }
  }
}
